import { useEffect, useRef } from 'react'
import { UserAvatar, type AvatarPerson } from '@/components/composites/user-avatar'
import { cn } from '@/lib/utils'

/**
 * The `@` picker list. Keyboard model and highlight state live in `mention-keyboard.ts`.
 */

export function MentionList({
  id,
  optionId,
  people,
  highlighted,
  onHighlight,
  onChoose,
  className,
}: {
  id: string
  optionId: (index: number) => string
  people: readonly AvatarPerson[]
  highlighted: number
  onHighlight: (index: number) => void
  onChoose: (person: AvatarPerson) => void
  className?: string
}) {
  const listRef = useRef<HTMLUListElement>(null)

  // Keep the highlighted row visible when the arrows walk past the list's scroll edge.
  useEffect(() => {
    const row = listRef.current?.children.item(highlighted)
    row?.scrollIntoView?.({ block: 'nearest' })
  }, [highlighted, people])

  return (
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      aria-label="People you can mention"
      className={cn(
        'absolute z-dropdown max-h-56 overflow-y-auto',
        'rounded-md border border-border bg-card p-1 shadow-e2',
        className,
      )}
    >
      {people.map((person, i) => (
        <li
          key={person.id}
          id={optionId(i)}
          role="option"
          aria-selected={i === highlighted}
          // `mouseDown`, not `click`: blur fires first otherwise and the list is gone
          // before the click resolves.
          onMouseDown={(e) => {
            e.preventDefault()
            onChoose(person)
          }}
          // `mouseMove` rather than `mouseEnter`: a row scrolled under a still pointer must not
          // steal the highlight from the keyboard.
          onMouseMove={() => {
            if (i !== highlighted) onHighlight(i)
          }}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-sm select-none',
            i === highlighted ? 'bg-brand-soft text-brand-dark' : 'text-foreground',
          )}
        >
          <UserAvatar person={person} size="xs" />
          <span className="truncate">{person.displayName}</span>
        </li>
      ))}
    </ul>
  )
}
